import { useCallback, useMemo, useEffect } from 'react';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { asUTCDate, asDateInTZ } from '@/utilities/convertToISO';
import geocodeByLatLng from '@/utilities/geocodeByLatLng';
import { v4 as uuid } from 'uuid';

import { DEFAULT_ITEM, DEFAULT_HAUL_AWAY, EDIT_ORDER_TABS } from '../constants';
import { enrich, swapReturnAttr } from '../utilities/processOrders';
import {
    addServiceLine,
    adjustServiceGroupQuantity,
    applyCatalogEntryToServiceGroup,
    deleteServiceGroup as deleteServiceGroupLines,
    setServiceGroupServiceTime,
} from '../utilities/serviceLines';

import { useSwappedAttributes } from '.';
import { useClientUser } from '@/hooks';
import { calculateOrderHash, genAttributes, ORDER_UPDATED_HASH_ATTRS } from '@onward-delivery/core';

export const useEditModalDynamicFields = (clone, modifyClone) => {
    const { special_instructions_settings } = useClientUser();

    const { is_custom, full_address, address, unit, location, location_info, location_type, street, city, state, zip } =
        useSwappedAttributes(clone);
    const {
        is_custom: pu_is_custom,
        full_address: pufull_address,
        address: puaddress,
        unit: puunit,
        location: pulocation,
        location_info: pulocation_info,
        street: pustreet,
        city: pucity,
        state: pustate,
        zip: puzip,
    } = useSwappedAttributes(clone, true);

    useEffect(() => {
        if (clone) {
            const order_updated_hash = calculateOrderHash(clone);

            modifyClone((prev) => {
                return { ...prev, order_updated_hash };
            });
        }
    }, [
        clone?.itemsByOrderId,
        clone?.services,
        clone?.references,
        ...(ORDER_UPDATED_HASH_ATTRS || []).map((attr) => {
            return clone?.[attr];
        }),
    ]);

    //location_info
    useEffect(() => {
        const calc = (location, unit) => {
            let rv = null;
            if (location === 'Residence') {
                if (unit?.length > 0) {
                    rv = 'Apartment';
                } else {
                    rv = 'House';
                }
            }

            return rv;
        };

        if (clone) {
            modifyClone((prev) => {
                let dropoff = calc(clone[location], clone[unit]);
                let pickup = calc(clone[pulocation], clone[puunit]);

                return { ...prev, [pulocation_info]: pickup, [location_info]: dropoff };
            });
        }
    }, [clone[unit], clone[location], clone[puunit], clone[pulocation], location_info, pulocation_info]);

    //is_middle_mile state/zip mod
    useEffect(() => {
        if (clone && clone.is_middle_mile) {
            modifyClone((prev) => {
                return {
                    ...prev,
                    [pucity]: clone[city],
                    [pustate]: clone[state],
                    [puzip]: clone[zip],
                };
            });
        }
    }, [clone.is_middle_mile, clone[state], clone[zip], clone[city], pucity, puzip, pustate]);

    // order-level special instructions
    useEffect(() => {
        const locationType = clone?.[location_type];
        if (!locationType) return;
        if (clone?.special_instructions) return;
        if (clone?.order_type === 'return' || clone?.order_type === 'wh_return') return;

        const specialInstructions = special_instructions_settings?.[locationType] || '';
        if (!specialInstructions) return;
        modifyClone((prev) => {
            // Re-check inside the setter in case a value landed between render and commit
            if (prev?.special_instructions) return prev;
            return { ...prev, special_instructions: specialInstructions };
        });
    }, [clone.order_type, clone?.[location_type], clone?.special_instructions, special_instructions_settings]);

    //address / full_address
    useEffect(() => {
        const calc = (fields) => fields.filter((val) => val && val.length > 0).join(', ');

        if (clone) {
            modifyClone((prev) => {
                const custompu = calc([clone[pustreet], clone[pucity], clone[pustate], clone[puzip]]);
                const customdo = calc([clone[street], clone[city], clone[state], clone[zip]]);
                const fullpu = calc([clone[pustreet], clone[puunit], clone[pucity], clone[pustate], clone[puzip]]);
                const fulldo = calc([clone[street], clone[unit], clone[city], clone[state], clone[zip]]);

                return {
                    ...prev,
                    [pufull_address]: fullpu,
                    [full_address]: fulldo,
                    ...(clone[pu_is_custom]
                        ? {
                              [puaddress]: custompu,
                          }
                        : {}),
                    ...(clone[is_custom]
                        ? {
                              [address]: customdo,
                          }
                        : {}),
                };
            });
        }
    }, [
        clone[address],
        clone[city],
        clone[full_address],
        clone[is_custom],
        clone[pu_is_custom],
        clone[puaddress],
        clone[pucity],
        clone[pufull_address],
        clone[pustate],
        clone[pustreet],
        clone[puunit],
        clone[puzip],
        clone[state],
        clone[street],
        clone[unit],
        clone[zip],
    ]);

    //order_type
    useEffect(() => {
        if ((clone?.itemsByOrderId || []).length > 0) {
            let next_type = 'delivery';

            const [containsDelivery, containsReturn] = clone.itemsByOrderId.reduce(
                ([containsDelivery, containsReturn], item) => {
                    return [containsDelivery || !item.is_return, containsReturn || item.is_return];
                },
                [false, false]
            );

            if (containsDelivery && containsReturn) {
                next_type = 'exchange';
            } else if (containsReturn) {
                next_type = 'return';
            }

            modifyClone((prev) => {
                const NO_OP = ['service', 'wh_inventory', 'wh_return', 'will_call'];
                if (prev?.order_type === next_type || NO_OP.includes(prev?.order_type)) {
                    return prev;
                }

                let swapped = {};
                if ([next_type, prev?.order_type].includes('return')) {
                    swapped = swapReturnAttr(prev);
                }

                return { ...prev, ...swapped, order_type: next_type };
            });
        }
    }, [clone.itemsByOrderId]);

    //adjust respective date strings if zipcode changes
    const fixTz = (date) => {
        const utc = new Date(asUTCDate(date).setUTCHours(0, 0, 0, 0)).toISOString();
        const tz = clone[zip] ? zipcode_to_timezone.lookup(clone[zip]) : 'America/New_York';
        return asDateInTZ(utc, tz).toISOString();
    };

    useEffect(() => {
        if (clone) {
            let converted = {};
            modifyClone((prev) => {
                if (prev.delivery_date) {
                    converted.delivery_date = fixTz(prev.delivery_date);
                }
                if (prev.warehouse_estimated_delivery_date) {
                    converted.warehouse_estimated_delivery_date = fixTz(prev.warehouse_estimated_delivery_date);
                }
                if (prev.warehouse_estimated_ship_date) {
                    converted.warehouse_estimated_ship_date = fixTz(prev.warehouse_estimated_ship_date);
                }
                if (prev.preferred_delivery_date) {
                    converted.preferred_delivery_date = fixTz(prev.preferred_delivery_date);
                }
                if (prev.estimated_delivery_range_start) {
                    converted.estimated_delivery_range_start = fixTz(prev.estimated_delivery_range_start);
                }
                if (prev.estimated_delivery_range_end) {
                    converted.estimated_delivery_range_end = fixTz(prev.estimated_delivery_range_end);
                }
                if (prev.alternative_delivery_dates) {
                    converted.alternative_delivery_dates = prev.alternative_delivery_dates.map((date) => fixTz(date));
                }

                return { ...prev, ...converted };
            });
        }
    }, [clone[zip]]);
};

export const middleMileOriginFields = (origin = {}) =>
    [
        'middle_mile_origin_name',
        'middle_mile_origin_phone',
        origin.street,
        origin.city,
        origin.state,
        origin.zip,
        origin.location_type,
        origin.location,
        origin.lat,
        origin.long,
    ].filter(Boolean);

export const genAttributesPerTab = ({ pickup, contact, origin = genAttributes({}, 'ORIGIN') }) => {
    return {
        [EDIT_ORDER_TABS.ORIGIN]: middleMileOriginFields(origin),
        [EDIT_ORDER_TABS.PICKUP]: [
            'pickup_name',
            'pickup_phone',
            pickup.address,
            pickup.city,
            pickup.state,
            pickup.street,
            pickup.zip,
            pickup.location_type,
            pickup.location,
            pickup.lat,
            pickup.long,
        ],
        [EDIT_ORDER_TABS.CUSTOMER]: [
            'dropoff_name',
            'dropoff_phone',
            'preferred_delivery_date',
            'alternative_delivery_dates',
            'estimated_delivery_range_start',
            'estimated_delivery_range_end',
            contact.address,
            contact.city,
            contact.state,
            contact.street,
            contact.zip,
            contact.lat,
            contact.long,
            contact.location_type,
            contact.location,
        ],
        [EDIT_ORDER_TABS.ORDER_DETAILS]: ['po_number', 'shipper_id'],
        [EDIT_ORDER_TABS.ITEMS]: ['itemsByOrderId'],
        [EDIT_ORDER_TABS.SERVICES]: ['services'],
        [EDIT_ORDER_TABS.HAUL_AWAY]: [],
        [EDIT_ORDER_TABS.WAREHOUSE]: ['warehouse_delivery_status', 'warehouse_estimated_ship_date'],
    };
};

export const useEditModalTabFields = (order) => {
    const contact = useSwappedAttributes(order);
    const pickup = useSwappedAttributes(order, true);
    const origin = useMemo(() => genAttributes(order || {}, 'ORIGIN'), [order]);

    return useMemo(() => {
        return genAttributesPerTab({
            contact,
            pickup,
            origin,
        });
    }, [contact, pickup, origin]);
};

export const useEditModalCallbacks = (state, callbacks) => {
    const { circles } = useClientUser();
    const { is_custom, lat, long, address, city, state: do_state, street, zip } = useSwappedAttributes(state.clone);

    const makeDirty = useCallback((attr) => {
        callbacks.setDirty((prev) => ({ ...prev, ...Object.fromEntries(attr.map((attr) => [attr, true])) }));
    }, []);

    const makeItemDirty = useCallback((idx, attr) => {
        callbacks.setDirty((prev) => {
            const clone = [...(prev.itemsByOrderId || [])];
            clone[idx] = { ...(clone[idx] || []), ...Object.fromEntries(attr.map((attr) => [attr, true])) };

            return {
                ...prev,
                itemsByOrderId: clone,
            };
        });
    }, []);

    const modifyOrder = useCallback((changes) => {
        // Assigning a carrier by hand is a DIRECT choice, and it has to say so: ingest re-reads
        // whatever the review page posts back and will not infer provenance from carrier_id.
        const patch =
            'carrier_id' in changes
                ? { ...changes, carrier_assignment_source: changes.carrier_id ? 'DIRECT' : null }
                : changes;
        callbacks.modifyClone((prev) => ({ ...prev, ...patch }));
    }, []);

    const applyItemOperations = useCallback((idx, changes, opt = { type: 'item' }) => {
        callbacks.modifyClone((prev) => {
            const attr = opt.type === 'item' ? 'itemsByOrderId' : 'haulaway_items';
            const clone = [...prev[attr]];
            const replacement = {
                ...clone[idx],
                ...Object.fromEntries(Object.entries(changes).map(([attr, callback]) => [attr, callback(clone[idx])])),
            };
            clone.splice(idx, 1, replacement);

            return {
                ...prev,
                [attr]: clone,
            };
        });
    }, []);

    const applyOrderOperations = useCallback((changes) => {
        callbacks.modifyClone((prev) => {
            const modifications = Object.fromEntries(
                Object.entries(changes).map(([attr, callback]) => [attr, callback(prev)])
            );

            return {
                ...prev,
                ...modifications,
            };
        });
    }, []);

    const addItem = useCallback(
        (opt = { type: 'item' }) => {
            callbacks.modifyClone((prev) => {
                const attr = opt.type === 'item' ? 'itemsByOrderId' : 'haulaway_items';
                const { location } = genAttributes(prev, 'DROPOFF');
                const BASE = {
                    item_type: prev.freight_type,
                    ...(prev.order_id ? { order_id: prev.order_id } : {}),
                    is_return: prev.order_type === 'return',
                    ...(prev.freight_type === 'pallet'
                        ? {
                              description: 'Pallet',
                              length: 48,
                              width: 40,
                          }
                        : {}),
                    ...(!!circles?.['import-lock-wg-light-assembly']
                        ? {
                              assembly_type: prev[location] === 'whiteGlove' ? 'minimal_assembly' : 'no_assembly',
                          }
                        : {}),
                };

                return {
                    ...prev,
                    [attr]: [
                        ...(prev[attr] || []),
                        opt.type === 'item' ? { ...DEFAULT_ITEM, ...BASE } : { ...DEFAULT_HAUL_AWAY, ...BASE },
                    ],
                };
            });
        },
        [circles]
    );

    const copyItem = useCallback((item, opt = { type: 'item' }) => {
        callbacks.modifyClone((prev) => {
            const attr = opt.type === 'item' ? 'itemsByOrderId' : 'haulaway_items';
            const { item_id, ...restItem } = item;
            const newItem = {
                pallet: null,
                pallet_id: null,
                item_sku_status: null,
                exceptions: [],
                item_exception: null,
                ...restItem,
            };

            return {
                ...prev,
                [attr]: [...(prev[attr] || []), newItem],
            };
        });
    }, []);

    const editItem = useCallback((idx, item, opt = { type: 'item' }) => {
        callbacks.modifyClone((prev) => {
            const attr = opt.type === 'item' ? 'itemsByOrderId' : 'haulaway_items';
            const clone = [...(prev[attr] || [])];
            const replacement = { ...clone[idx], ...item };
            clone.splice(idx, 1, replacement);

            return {
                ...prev,
                [attr]: clone,
            };
        });
    }, []);

    // One state update for the whole set, so a row doesn't need the full items array to fan out.
    const applyDispositionToReturns = useCallback((disposition, exceptIdx) => {
        callbacks.modifyClone((prev) => ({
            ...prev,
            itemsByOrderId: (prev.itemsByOrderId || []).map((item, idx) =>
                item.is_return && idx !== exceptIdx ? { ...item, disposition } : item
            ),
        }));
    }, []);

    const editPallet = useCallback((idx, pallet_id, update = {}) => {
        callbacks.modifyClone((prev) => {
            const updatedItems = [...(prev.itemsByOrderId || [])].map((item, i) => {
                if (idx === i || (item.pallet_id && item.pallet_id === pallet_id)) {
                    return {
                        ...item,
                        pallet: {
                            ...(item.pallet || {}),
                            ...(update || {}),
                        },
                    };
                }
                return item;
            });

            return {
                ...prev,
                itemsByOrderId: updatedItems,
            };
        });
    }, []);

    const editNote = useCallback((note) => {
        callbacks.modifyClone((prev) => {
            const oldNotes = [...(prev?.notes || [])];
            const replacement = { ...(oldNotes?.[0] || {}), ...note };
            const newNotes = [replacement];

            return {
                ...prev,
                notes: newNotes,
            };
        });
    }, []);

    const addNote = useCallback((note) => {
        callbacks.modifyClone((prev) => {
            if (!note?.note_id) {
                if (prev?.note_id) {
                    note.note_id = prev.note_id;
                } else {
                    note.note_id = uuid();
                }
            }

            return {
                ...prev,
                notes: [...(prev?.notes || []), note],
            };
        });
    }, []);

    const deleteItem = useCallback((idx, opt = { type: 'item' }) => {
        callbacks.modifyClone((prev) => {
            const attr = opt.type === 'item' ? 'itemsByOrderId' : 'haulaway_items';
            const clone = [...prev[attr]];
            clone.splice(idx, 1);

            return {
                ...prev,
                [attr]: clone,
            };
        });
    }, []);

    const addReference = useCallback(() => {
        callbacks.modifyClone((prev) => ({
            ...prev,
            references: [...(prev.references || []), { key: '', value: '', label: '' }],
        }));
    }, []);

    const editReference = useCallback((idx, changes) => {
        callbacks.modifyClone((prev) => {
            const next = [...(prev.references || [])];
            next[idx] = { ...next[idx], ...changes };
            return { ...prev, references: next };
        });
    }, []);

    const deleteReference = useCallback((idx) => {
        callbacks.modifyClone((prev) => {
            const next = [...(prev.references || [])];
            next.splice(idx, 1);
            return { ...prev, references: next };
        });
    }, []);

    // Thin shells over the pure reducers in utilities/serviceLines.js.
    const addService = useCallback((service) => {
        callbacks.modifyClone((prev) => addServiceLine(prev, service));
    }, []);

    // The Services tab groups duplicate rows by code; these step a group's
    // total by ±1 / delete a whole code-group (keyed by code, not array index).
    const editServiceQuantity = useCallback((code, delta) => {
        callbacks.modifyClone((prev) => adjustServiceGroupQuantity(prev, code, delta));
    }, []);

    const deleteServiceGroup = useCallback((code) => {
        callbacks.modifyClone((prev) => deleteServiceGroupLines(prev, code));
    }, []);

    const editServiceTime = useCallback((code, serviceTime) => {
        callbacks.modifyClone((prev) => setServiceGroupServiceTime(prev, code, serviceTime));
    }, []);

    // A code just added to the catalog (quick add): fill its lines from the new entry.
    const applyCatalogService = useCallback((entry) => {
        callbacks.modifyClone((prev) => applyCatalogEntryToServiceGroup(prev, entry.code, entry));
    }, []);

    const enrichOrder = useCallback(
        async (attr, address, opts = {}) => {
            // Back-compat: callers that still pass `true`/`false` as the third
            // arg are routed to `{ pickup }`. New call sites should pass
            // `{ pickup, geocodeOverride }` explicitly.
            const normalizedOpts = typeof opts === 'boolean' ? { pickup: opts } : opts;
            const { pickup = false, geocodeOverride } = normalizedOpts;

            // When the address came from an autocomplete selection we already
            // have authoritative components + lat/lng resolved by placeId. Seed
            // the geocode cache with that result so `enrich()` skips the
            // redundant text-based round-trip. The text round-trip is what
            // silently dropped city/state/zip on places where Google's text
            // geocoder snapped to a route centroid (e.g. CDPs like Riverview
            // FL) even though the placeId resolved to a complete street_address.
            const seedCache =
                geocodeOverride && geocodeOverride.lat != null && geocodeOverride.lng != null
                    ? { [address]: geocodeOverride }
                    : {};

            const [{ geocodePartialMatch, geocodeFailed, ...enriched }, cache] = await enrich(
                { ...state.clone, [attr]: address },
                seedCache,
                { pickup }
            );

            callbacks.modifyClone(enriched);
            callbacks.setGeocache(cache);
            callbacks.setErrors((prev) => ({ ...prev, geocodePartialMatch, geocodeFailed }));

            return { errors: { geocodePartialMatch, geocodeFailed } };
        },
        [state.clone, state.geoCache]
    );

    const geocodeLatLong = useCallback(
        async (event) => {
            if (event?.latLng) {
                const x = event.latLng.lat();
                const y = event.latLng.lng();

                try {
                    let geocodeResults = await geocodeByLatLng({ lat: x, lng: y });
                    geocodeResults = geocodeResults || {
                        address: '',
                        city: '',
                        state: '',
                        street: '',
                        zip: '',
                    };

                    callbacks.modifyClone((prev) => {
                        return {
                            ...prev,
                            [lat]: x,
                            [long]: y,
                            ...(state.clone[is_custom]
                                ? {}
                                : {
                                      [address]: geocodeResults.address,
                                      [city]: geocodeResults.city,
                                      [do_state]: geocodeResults.state,
                                      [street]: geocodeResults.street,
                                      [zip]: geocodeResults.zip,
                                  }),
                        };
                    });
                } catch (e) {
                    console.error(e);
                    callbacks.onError('Geocode failed. Please try again.');
                }
            }
        },
        [state.clone]
    );

    return {
        addItem,
        addReference,
        addService,
        applyCatalogService,
        applyItemOperations,
        applyOrderOperations,
        deleteItem,
        deleteReference,
        deleteServiceGroup,
        editServiceQuantity,
        editServiceTime,
        editItem,
        editReference,
        enrichOrder,
        modifyOrder,
        geocodeLatLong,
        makeDirty,
        makeItemDirty,
        editPallet,
        applyDispositionToReturns,
        copyItem,
        editNote,
        addNote,
    };
};
